import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import { Badge, Box, Flex, Spinner } from "theme-ui";

import { getServicePlans, getServicePlanSiteCounts, getServicePlanLastActivity } from "api/ServicePlans";
import BasicCard from "components/ui/BasicCard";
import H5 from "components/typography/H5";
import P from "components/typography/P";
import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import { ChevronIcon } from "components/svg";
import { themevals } from "theme/themevals";

/**
 * MASTER_PLAN v3.4 §3.9 #21 — Client-detail Plans tab.
 *
 * Post-v3.4 correction, this tab is a SECONDARY pathway only: a
 * simple list of Maintenance Plans this client is part of, each row
 * a click-through to /maintenance/plans/:id. Same shape as the
 * sibling RFQs / Quotes / Invoices / Projects / POs tabs on the
 * client record — list-and-open, not create-and-manage.
 *
 * Explicitly removed from this tab per the v3.4 preamble:
 *   - "+ New Service Plan" primary action  (moved to /maintenance)
 *   - Stat cards, per-plan expandable Sites list, right rail
 *   - Import Sites affordance             (moved to Plan Detail header)
 *   - Close Plan affordance               (already on Plan Detail)
 *   - Inline SiteHistoryModal              (users open Plan Detail instead)
 *
 * Serves both admin and client viewers identically — nothing on this
 * tab is admin-gated, since every action lives on the pages a
 * click-through takes you to (which already handle their own
 * admin/client rendering).
 */
export default function PlansTab({ clientId }: { clientId: string }) {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<any[] | null>(null);
  const [siteCounts, setSiteCounts] = useState<Record<string, number>>({});
  const [lastActivity, setLastActivity] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (clientId) load();
    // eslint-disable-next-line
  }, [clientId]);

  const load = async () => {
    setLoading(true);
    const res = await getServicePlans({
      query: { filters: { clientId }, recordsPer: 100, sortBy: "updatedAt", sortDir: "desc" },
    });
    const items = Array.isArray(res?.items) ? res.items : [];
    setPlans(items);

    if (items.length) {
      const planIds = items.map((p: any) => p.id);
      // Parallel: site counts + real last-activity timestamps. Both
      // endpoints are ownership-filtered on the backend, so client
      // viewers only see counts/timestamps for plans their client
      // owns — no 403.
      const [counts, activity] = await Promise.all([
        getServicePlanSiteCounts({ data: { planIds } }),
        getServicePlanLastActivity({ data: { planIds } }),
      ]);
      setSiteCounts(counts || {});
      setLastActivity(activity || {});
    } else {
      setSiteCounts({});
      setLastActivity({});
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", paddingY: "1.5rem" }}>
        <Spinner />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ marginBottom: "20px" }}>
        <H5 fontFamily="Poppins" fontWeight={600} margin={0} padding={0}>
          Maintenance Plans
        </H5>
        <Subtext marginTop={"4px"} marginBottom={0} fontSize={"12px"} fontFamily={"Poppins"}>
          Plans this client is part of. Open a plan to manage its sites, cadence, and work orders.
        </Subtext>
      </Box>

      {(!plans || plans.length === 0) && (
        <BasicCard>
          <P marginTop={0} marginBottom={"6px"} color={themevals.colors.dkgrey}>
            This client isn&apos;t part of any Maintenance Plans yet.
          </P>
          <P marginTop={0} marginBottom={0} fontSize={"12px"} color={themevals.colors.dkgrey}>
            Create one from the{" "}
            <Span
              onClick={() => navigate("/maintenance")}
              sx={{
                cursor: "pointer",
                color: themevals.colors.info,
                textDecoration: "underline",
              }}
            >
              Maintenance module
            </Span>
            .
          </P>
        </BasicCard>
      )}

      {plans && plans.length > 0 && (
        <Box
          sx={{
            background: "white",
            border: "1px solid",
            borderColor: "inputOutline",
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0px 1px 2px 0px rgba(113, 125, 150, 0.03)",
          }}
        >
          {plans.map((plan, idx) => {
            const isTermBased = !!plan.endDate;
            const isLast = idx === plans.length - 1;
            const sitesCount = siteCounts[plan.id] || 0;
            const lastAt = lastActivity[plan.id];
            return (
              <Flex
                key={plan.id}
                onClick={() => navigate(`/maintenance/plans/${plan.id}`)}
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                  padding: "14px 18px",
                  borderBottom: isLast ? "none" : "1px solid",
                  borderColor: "inputOutline",
                  cursor: "pointer",
                  "&:hover": { background: "#FAFAF8" },
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Flex sx={{ alignItems: "center", gap: "10px" }}>
                    <Span
                      fontFamily={"Poppins"}
                      fontWeight={600}
                      fontSize={"14px"}
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {plan.name}
                    </Span>
                    <Badge as="span" variant={plan.status === "open" ? "success" : "info"}>
                      {plan.status === "open" ? "Open" : "Closed"}
                    </Badge>
                  </Flex>
                  <Subtext
                    marginTop={"4px"}
                    marginBottom={0}
                    fontSize={"12px"}
                    fontFamily={"Poppins"}
                  >
                    {isTermBased
                      ? `Term-based · target ${moment(plan.endDate).format("MMM D, YYYY")}`
                      : "Ongoing"}
                    {" · "}
                    {sitesCount} {sitesCount === 1 ? "location" : "locations"}
                    {" · "}
                    {lastAt
                      ? `last activity ${moment(lastAt).format("MMM D, YYYY")}`
                      : "no activity yet"}
                  </Subtext>
                </Box>
                <ChevronIcon
                  width="10px"
                  fill={themevals.colors.dkgrey}
                  transform="rotate(-90deg)"
                />
              </Flex>
            );
          })}
        </Box>
      )}

      {/* GAP-32 (mockup 10) — client-facing notification-posture
       * explainer. The mockup closes the client Plans view with a note
       * telling the client WHY they may never get proactive email
       * ("Your account is set to Corrigo-managed. All proactive emails
       * are off..."). Rather than hardcode "Corrigo-managed", derive the
       * real posture from the loaded plans' notificationConfig matrices
       * (same all-false -> silent / any-true -> on logic as the
       * Maintenance index GAP-30 line). Only shown when the client has
       * at least one plan — no plan, no posture to explain. */}
      {plans && plans.length > 0 && (
        <NotificationPostureNote plans={plans} />
      )}
    </Box>
  );
}

/**
 * GAP-32 — derives the account's proactive-email posture across all of
 * the client's plans and renders a short client-facing explainer. A
 * plan is "silent" when its notificationConfig exists and every value
 * is falsy; "on" when any value is true; "unset" when there's no config
 * (renders nothing for that plan). Across plans: all-silent -> emails
 * off; any-on -> some updates on; all-unset -> render nothing (no
 * posture configured, so no claim to make).
 */
function NotificationPostureNote({ plans }: { plans: any[] }) {
  let anyConfigured = false;
  let anyOn = false;
  for (const plan of plans) {
    const cfg = plan?.notificationConfig;
    if (cfg && typeof cfg === "object") {
      const values = Object.values(cfg);
      if (values.length > 0) {
        anyConfigured = true;
        if (values.some((v) => v === true)) anyOn = true;
      }
    }
  }

  // No plan has a configured notification matrix — nothing truthful to
  // say about proactive email, so render nothing.
  if (!anyConfigured) return null;

  const body = anyOn
    ? "You receive email updates for some plan events. To adjust which updates you get, contact your GetMulch account team."
    : "Proactive emails are off for your plans — updates are managed through your work-order system rather than direct email. Prefer to receive email updates? Contact your GetMulch account team.";

  return (
    <Box
      sx={{
        marginTop: "18px",
        padding: "14px 16px",
        background: "#F1EFE8",
        borderRadius: "8px",
      }}
    >
      <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={themevals.colors.dkgrey}>
        Notifications
      </Subtext>
      <P marginTop={"4px"} marginBottom={0} fontSize={"13px"} color={themevals.colors.black}>
        {body}
      </P>
    </Box>
  );
}
